import { DotsThreeVertical } from "@phosphor-icons/react";
import { useState } from "react";

import MemberPermissionsTag from "common/components/MemberPermissionsTag";
import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import { Button } from "common/components/ui/Button";
import { Dialog, DialogContent } from "common/components/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import { typedObjectEntries } from "common/helpers/utils";
import type { Member } from "common/types";

import { CLIENT_ACTIONS } from "../../../constants";
import { formatBillingDate } from "../../../utils";
import ClientActions from "../ClientActions";

export default function DesktopTable({ clients }: { clients: Member[] }) {
  const [actionState, setActionState] = useState<{
    isOpen: boolean;
    action: keyof typeof CLIENT_ACTIONS;
    selectedClient: Member;
  }>({ isOpen: false, action: "remove", selectedClient: undefined });

  const { isOpen, action, selectedClient } = actionState;

  return (
    <>
      <Table>
        <thead>
          <tr>
            <TableHead>Client</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Date Added</TableHead>
            <TableHead />
          </tr>
        </thead>

        <tbody>
          {clients.map((client) => {
            const {
              token,
              first_name: firstName,
              last_name: lastName,
              email,
              created_at: createdAt,
              permissions,
              role,
            } = client;

            return (
              <TableRow key={token}>
                <TableCell className="flex items-center gap-2.5">
                  <AimfoxAvatar firstName={firstName} lastName={lastName} />

                  <div className="flex flex-col items-start gap-1">
                    <span className="text-md-bold text-black-950">
                      {firstName} {lastName}
                    </span>

                    <span className="inline-block max-w-[40ch] overflow-hidden text-ellipsis text-xs-medium text-black-500">
                      {email}
                    </span>
                  </div>
                </TableCell>

                <TableCell>
                  <MemberPermissionsTag permissions={permissions} role={role} />
                </TableCell>

                <TableCell className="text-button-xs text-black-950">
                  {formatBillingDate(createdAt)}
                </TableCell>

                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        intent="iconOnly"
                        variant="secondary-black"
                        size="sm"
                      >
                        <DotsThreeVertical />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" size="sm">
                      <DropdownMenuList>
                        {typedObjectEntries(CLIENT_ACTIONS).map(
                          ([key, { icon: Icon, label, ...rest }]) => (
                            <DropdownMenuItem
                              key={key}
                              onClick={() =>
                                setActionState({
                                  isOpen: true,
                                  action: key,
                                  selectedClient: client,
                                })
                              }
                              variant={
                                "isDanger" in rest ? "danger" : "default"
                              }
                            >
                              <Icon />
                              {label}
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuList>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>

      {selectedClient && (
        <Dialog
          open={isOpen}
          onOpenChange={(isOpen) => setActionState({ ...actionState, isOpen })}
        >
          <DialogContent size="md">
            <ClientActions
              action={action}
              member={selectedClient}
              onClose={() => setActionState({ ...actionState, isOpen: false })}
              onBack={() => setActionState({ ...actionState, isOpen: false })}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
